// How Raf thinks.
//
// Not a greedy step toward Nick — that version walks into walls, gets stuck
// behind blocks, and you learn to abuse it in about a minute. Raf runs a real
// breadth-first search across the maze and descends the resulting distance
// field, so every move is on a genuine shortest path. What makes him beatable
// is not stupidity: it is the guard phase, the cost of turning round, and the
// fact that you can see which junction he is heading for.

import { DIR_LIST, opposite, sameDir } from './maze.js';

/**
 * Three appetites. Speeds are multipliers on Nick's; durations are seconds.
 *
 * `overshoot` is the one that changes the feel rather than the numbers: at 0
 * Raf walks to where you ARE, which is always where you just were. Above 0 he
 * aims at the ground BEYOND you, on the far side from himself — so he commits
 * to the space you would have to retreat into instead of falling in behind you.
 * Several Rafs need no coordination to surround you: they are in different
 * places, so "past Nick, away from me" already points them different ways.
 *
 * `guard` is how long he spends minding the pizza rather than chasing. It is
 * not a rest — he parks on food you still have to come and get.
 */
export const DIFFICULTIES = [
  {
    id: 'peckish',
    name: 'Peckish',
    blurb: 'Raf ambles, and spends long stretches minding the pizza. Room to learn the maze.',
    speed: 0.74, guard: 8, chase: 12, fright: 9, overshoot: 0, ramp: 0.02, frightFloor: 4,
  },
  {
    id: 'hungry',
    name: 'Hungry',
    blurb: 'Level pace. He cuts to the ground behind you, so greed costs.',
    speed: 0.94, guard: 6, chase: 20, fright: 6.5, overshoot: 2, ramp: 0.03, frightFloor: 2.5,
  },
  {
    id: 'ravenous',
    name: 'Ravenous',
    blurb: 'Faster than you are, and he aims at your way out rather than at you.',
    speed: 1.06, guard: 4, chase: 28, fright: 4.5, overshoot: 4, ramp: 0.04, frightFloor: 1.5,
  },
];

export const difficultyById = (id) => DIFFICULTIES.find((d) => d.id === id) ?? DIFFICULTIES[1];

/** Nick's speed in tiles per second. Everything else is a multiple of it. */
export const NICK_SPEED = 6.8;

/**
 * Tempo.
 *
 * This scales TIME, not just the movement speeds — one factor applied to the
 * whole simulation, so Nick, every Raf, the wave timers and the fright window
 * all slow by exactly the same amount. Slowing only the movement would leave
 * the six-second wave timers running at full speed, so you would cover less
 * maze per wave and the game would quietly get harder as it got slower. This
 * way it is the same game, played at a different tempo.
 */
export const SPEEDS = [
  { id: 'slow', name: 'Slow', factor: 0.6, blurb: 'Two-thirds pace. Room to think, and to watch a crowd of Rafs work.' },
  { id: 'normal', name: 'Normal', factor: 1, blurb: 'As designed.' },
  { id: 'fast', name: 'Fast', factor: 1.35, blurb: 'Quicker than is comfortable.' },
];
export const speedById = (id) => SPEEDS.find((s) => s.id === id) ?? SPEEDS[1];

/** How many Rafs, and how many extra pies, the settings will allow. */
export const MAX_GHOSTS = 50;
export const MAX_EXTRA_PIES = 50;

/** Raf gets faster each level; Nick does not. That is the difficulty curve. */
export function rafSpeed(diff, level, mode) {
  if (mode === 'eaten') return NICK_SPEED * 2.4;
  const ramped = diff.speed * (1 + diff.ramp * (level - 1));
  const mult = mode === 'fright' ? Math.min(0.62, ramped * 0.62) : Math.min(1.35, ramped);
  return NICK_SPEED * mult;
}

/** The frightened window shrinks with the level, but never to nothing. */
export function frightSeconds(diff, level) {
  return Math.max(diff.frightFloor, diff.fright - (level - 1) * 0.45);
}

/** Guarding and chasing take turns, and both shorten as the level climbs. */
export function waveSeconds(diff, level, wave) {
  const decay = Math.max(0.45, 1 - (level - 1) * 0.07);
  return (wave === 'guard' ? diff.guard : diff.chase) * decay;
}

/** A Raf that doubles back stands still for this long first. */
export const REVERSE_STALL = 0.22;

const tileOf = (k) => {
  const [x, y] = k.split(',').map(Number);
  return { x, y };
};

/** The tile a ghost is walking toward, which is the whole of its personality. */
export function targetTile(mode, nick, diff, board, ghost, food) {
  if (mode === 'eaten') return { ...board.rafStart };
  if (mode === 'guard') {
    // Mind a pizza rather than retreat to a corner. Sulking in a fixed corner
    // hands the player a safe half of the maze and a stopwatch; standing on
    // food they still need means the clock runs on THEM.
    //
    // A Raf keeps the slice he picked until somebody eats it, so the target
    // does not jump about every time a pizza goes — and the fast path here is
    // the one that runs almost every time.
    if (ghost?.guarding && food?.has(ghost.guarding)) return tileOf(ghost.guarding);
    const keys = food ? [...food.keys()] : [];
    if (!keys.length) return { ...board.rafStart };
    const pick = keys[((ghost?.corner ?? 0) * 7 + 3) % keys.length];
    if (ghost) ghost.guarding = pick;
    return tileOf(pick);
  }

  const tile = { x: Math.round(nick.x), y: Math.round(nick.y) };
  if (!diff.overshoot || !ghost) return tile;

  // Past Nick, on the far side from this Raf. Walking at where somebody is
  // means arriving where they were; this aims at the ground they would have to
  // give up.
  //
  // The approach is LATCHED, and that is not a detail. Recomputing it every
  // decision means the moment a Raf draws level the sign flips, the target
  // jumps to the other side of Nick, and he turns round — then flips back.
  // Forty reversals in sixty steps, which reads as a broken toy rather than
  // as pursuit. So he picks his line while he is still a way off and commits
  // to it, and only re-aims once the distance has opened up again.
  const dx = tile.x - Math.round(ghost.x);
  const dy = tile.y - Math.round(ghost.y);
  if (!ghost.approach || Math.abs(dx) + Math.abs(dy) > diff.overshoot * 2) {
    ghost.approach = { x: Math.sign(dx), y: Math.sign(dy) };
  }
  const a = ghost.approach;
  if (!a.x && !a.y) return tile;
  // Stepping the overshoot back a tile at a time keeps the target inside the
  // maze — otherwise Raf aims into rock and the field comes back empty.
  for (let n = diff.overshoot; n > 0; n -= 1) {
    const x = board.wrapX(tile.x + a.x * n);
    const y = tile.y + a.y * n;
    if (board.walkable(x, y)) return { x, y };
  }
  return tile;
}

/**
 * One decision, made only at a tile centre.
 *
 * Frightened ghosts ascend the field from Nick instead of descending it — the
 * same search, run backwards. That reads as panic and is genuinely hard to
 * corner, because a ghost will take a longer route if it puts more maze between
 * you.
 *
 * `fields` is a per-tick cache. With fifty ghosts chasing one target that turns
 * fifty breadth-first searches per frame into one.
 */
export function decide(ghost, nick, mode, diff, board, fields, food) {
  const tile = { x: Math.round(ghost.x), y: Math.round(ghost.y) };
  const flee = mode === 'fright';
  const from = flee
    ? { x: Math.round(nick.x), y: Math.round(nick.y) }
    : targetTile(mode, nick, diff, board, ghost, food);

  const cacheKey = `${from.x},${from.y}`;
  let field = fields?.get(cacheKey);
  if (!field) {
    field = board.distanceField(from);
    fields?.set(cacheKey, field);
  }

  const back = opposite(ghost.dir);
  const options = [];
  for (const dir of DIR_LIST) {
    const nx = board.wrapX(tile.x + dir.x);
    const ny = tile.y + dir.y;
    if (!board.walkable(nx, ny)) continue;
    const d = board.distAt(field, nx, ny);
    if (d < 0) continue;
    options.push({ dir, d, reverse: sameDir(dir, back) });
  }
  if (!options.length) return back ?? ghost.dir;

  // Turning round is allowed, but it COSTS. A flat ban is the tidier rule and
  // it is also somebody else's; letting a Raf reverse whenever he likes makes
  // him oscillate in a corridor and stop reading as pursuit. Charging him a
  // beat of hesitation gets both: he can double back when doubling back is
  // genuinely right, and the pause is your warning that he has.
  let best = options[0];
  for (const o of options) {
    if (flee ? o.d > best.d : o.d < best.d) best = o;
  }
  if (best.reverse && ghost) ghost.stall = REVERSE_STALL;
  return best.dir;
}
